import { useLazyQuery, useMutation } from '@apollo/client';
import { FC } from 'react';
import { FileUploadWithGenerateVerificationStatusReportButton } from './file-upload-with-generate-verification-status-report-button';
import { CasesSendReportV1SendCaseFileUploadWithGenerateVerificationStatusReportButtonContainerSendReportCaseV1Document, CasesSendReportV1SendCaseFileUploadWithGenerateVerificationStatusReportButtonContainerSendReportCaseV1ImageFileCreateAuthHeaderDocument, CasesSendReportV1SendCaseFileUploadWithGenerateVerificationStatusReportButtonContainerSendReportCaseV1PdfFileCreateAuthHeaderDocument, CasesSendReportV1SendCaseFileUploadWithGenerateVerificationStatusReportButtonContainerSendReportCaseV1UpdateDocument, FileInfo, SendReportCaseV1UpdateInput } from '../../../../../../generated';
import { GetSendReportAuthHeader } from './send-report-get-auth-header';
import { AuthResult, AzureBlobResponse } from '../../../../../shared/file-upload/azure-upload';

interface FileUploadWithGenerateVerificationStatusReportButtonContainerProps {
  caseId: string;
  title: string;
  buttonText?: string;
  description?: string;
  permittedContentTypes?: string[];
  permittedExtensions?: string[];
  isPreviewOnly?: boolean;
  setIsEditing?: React.Dispatch<React.SetStateAction<boolean>>;
  verificationFileName: string;
  isShowFileName?: boolean;
  verificationFileNames: Map<string, string>;
  isCoverLetter: boolean;
  isUploadPreviewImage?: boolean;
}

export const FileUploadWithGenerateVerificationStatusReportButtonContainer: FC<FileUploadWithGenerateVerificationStatusReportButtonContainerProps> = ({
  isPreviewOnly = false,
  ...props
}) => {
  const [getAuthHeaderForPdfFile] = useMutation(
    CasesSendReportV1SendCaseFileUploadWithGenerateVerificationStatusReportButtonContainerSendReportCaseV1PdfFileCreateAuthHeaderDocument
  );

  const [getAuthHeaderForImageFile] = useMutation(
    CasesSendReportV1SendCaseFileUploadWithGenerateVerificationStatusReportButtonContainerSendReportCaseV1ImageFileCreateAuthHeaderDocument
  );

  const [getFilePreviewData] = useLazyQuery(CasesSendReportV1SendCaseFileUploadWithGenerateVerificationStatusReportButtonContainerSendReportCaseV1Document, {
    fetchPolicy: 'network-only'
  });

  const [updatedSendCaseV1] = useMutation(CasesSendReportV1SendCaseFileUploadWithGenerateVerificationStatusReportButtonContainerSendReportCaseV1UpdateDocument);

  const handleUpdateAfterUploadSuccess = async (uploadedData: AzureBlobResponse) => {
    const assetPath = 'private';
    const fieldName = "verificationStatusReport";

    const input: SendReportCaseV1UpdateInput = {
      id: props.caseId,
      assets: {
        [assetPath]: {
          [fieldName]: uploadedData.blobUrl,
          [`${fieldName}Version`]: uploadedData.versionId
        }
      }
    };
    await updatedSendCaseV1({
      variables: {
        input
      }
    });
  };

  const handleAuthorizeRequest = async (fileData: File, filename: string): Promise<AuthResult> => {
    return GetSendReportAuthHeader(props.caseId, fileData, filename, getAuthHeaderForPdfFile, getAuthHeaderForImageFile);
  };

  const handleFetchFilePreviewData = async (filename: string): Promise<FileInfo> => {
    const previewData = await getFilePreviewData({
      variables: {
        sendReportCaseV1Id: props.caseId,
        fileName: filename
      }
    });
    return previewData?.data?.sendReportCaseV1?.fileByFileName as FileInfo;
  };

  return (
    <FileUploadWithGenerateVerificationStatusReportButton
      caseId={props.caseId}
      title={props.title}
      buttonText={props.buttonText}
      description={props.description}
      permittedContentTypes={props.permittedContentTypes}
      permittedExtensions={props.permittedExtensions}
      isPreviewOnly={isPreviewOnly}
      setIsEditing={props.setIsEditing}
      onAuthorizeRequest={(fileData: File) => handleAuthorizeRequest(fileData, props.verificationFileName)}
      fetchFilePreviewData={() => handleFetchFilePreviewData(props.verificationFileName)}
      onUploadSuccess={(response: AzureBlobResponse) => handleUpdateAfterUploadSuccess(response)}
      isShowFileName={props.isShowFileName}
      isUploadingPreviewImage={false}
      isCoverLetter={props.isCoverLetter}
      verificationFileNames={props.verificationFileNames}
      verificationFileName={props.verificationFileName}
    />
  );
};
